<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  SearchMdIcon,
  Edit03Icon,
  Trash03Icon
} from 'front-library'

import {
  MasterConceptGroup,
  getMasterConceptGroups,
  deleteMasterConceptGroup
} from '@/app/core/services/master-concept-groups'

usePayrollChange(() => window.location.reload())

const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const acceptQuestion = ref('')

const loading = ref(false)
const messageLoading = ref('')

const search = ref('')
const conceptGroupForDelete = ref('')

const tableConceptGroup = ref(null)

const columns = ref([
  { name: 'code', title: 'Code', table: 'code' },
  { name: 'description', title: 'Description', table: 'description' },
  {
    name: 'updated_at',
    title: t('masterConcepts.list.lastUpdate'),
    table: 'updated_at'
  },
  { name: 'actions', title: '' }
])

const rows = ref<MasterConceptGroup[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

const createConceptGroup = () => {
  void router.push({ name: 'CreateMasterConceptGroup' })
}

const updateConceptGroup = (id: string) => {
  void router.push({ name: 'UpdateMasterConceptGroup', params: { id } })
}

const getConceptGroup = (id: string) => {
  void router.push({ name: 'GetMasterConceptGroup', params: { id } })
}

const removeConceptGroup = (values: any) => {
  showModal.value = true
  titleModal.value = `Eliminar Grupo ${values.code}?`
  descriptionModal.value = t('modal.description')
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('table.action.delete')
  })
  conceptGroupForDelete.value = values.id
}

const deleteConfirm = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  showModal.value = false

  await deleteMasterConceptGroup(conceptGroupForDelete.value)
    .then(() => {
      notifications.addNotification({
        title: t('notification.removed'),
        text: 'Grupo eliminado correctamente',
        type: 'success',
        time: 10000
      })
      void conceptGroups()
    })
    .catch((error: any) => {
      notificationError(error, 'masterConceptGroups', t, notifications, loading)
    })
}

const closeModal = () => {
  showModal.value = false
}

const conceptGroups = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')

  try {
    const data: any = await getMasterConceptGroups(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )

    rows.value = data[0].results
    totalRows.value = data[0].totalItems
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'masterConceptGroups', t, notifications, loading)
  }
}

onMounted(() => {
  void conceptGroups()
})
</script>

<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    typeAlert="question"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="deleteConfirm" />

  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p class="text-3xl font-semibold text-shadow">Grupos de conceptos</p>
        <p class="text-gray-500 text-shadow">
          Lista de grupos que se utilizan para agrupar conceptos similares y
          facilitar su uso en las fórmulas.
        </p>
      </div>

      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="conceptGroups" />
          <DBtn
            @click.prevent="conceptGroups"
            v-html="t('form.button.search')" />
        </div>

        <DBtn
          v-if="store.hasPermission('concept_group:create')"
          :icon="'PlusIcon'"
          type="button"
          @click="createConceptGroup()">
          Crear Grupo
        </DBtn>
      </div>
    </div>

    <DTable
      ref="tableConceptGroup"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), conceptGroups()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), conceptGroups()">
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('concept_group:get')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getConceptGroup(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>

          <Popper
            v-if="store.hasPermission('concept_group:update')"
            arrow
            offsetDistance="12"
            :content="t('table.action.edit')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="updateConceptGroup(data.id)">
              <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>

          <Popper
            v-if="store.hasPermission('concept_group:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="removeConceptGroup(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
